/**
 * Design 2: the Freight Status board — a broad, all-drivers overview (status,
 * load, customer, appointment/PTA risk, HOS, tags) for planners who want to
 * scan the whole fleet rather than be served individual tasks.
 *
 * `FreightStatusBoard` is the reusable board (filters + sort + table) driven by
 * injected driver data — it backs both the in-app "Freight Status" driver tab
 * and the standalone `FreightStatusView` (which adds the My/All/Freight tab
 * chrome for the prototype page).
 */

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";

import { accent, text, zinc } from "./colors";
import { Chip, Pill, StatusDot, TagChip } from "./components/primitives";
import { DRIVERS } from "./data";
import type { Driver, RiskInfo } from "./types";

type DriverTab = "my" | "all" | "freight";
type FilterKey = "all" | "at_risk" | "live" | "drop" | "empty" | "breakdown" | "available";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "at_risk", label: "At risk" },
  { key: "live", label: "Live unload" },
  { key: "drop", label: "Drop" },
  { key: "empty", label: "Empty call" },
  { key: "breakdown", label: "Breakdown" },
  { key: "available", label: "Available" },
];

const driverIsAtRisk = (d: Driver) => Boolean(d.appointmentRisk?.atRisk || d.pta?.risk.atRisk);

function matchesFilter(d: Driver, filter: FilterKey): boolean {
  switch (filter) {
    case "at_risk":
      return driverIsAtRisk(d);
    case "live":
      return d.loadType === "Live unload";
    case "drop":
      return d.loadType === "Drop";
    case "empty":
      return d.tags.includes("empty_call") || d.loadType === "Empty";
    case "breakdown":
      return d.status === "BD" || d.tags.includes("ddi");
    case "available":
      return d.status === "AC";
    default:
      return true;
  }
}

function ptaMinutes(d: Driver): number {
  const time = d.pta?.time;
  if (!time) return Number.POSITIVE_INFINITY;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

const COLUMNS =
  "minmax(150px, 1.4fr) 56px 96px minmax(130px, 1.2fr) 108px 130px 120px 72px 96px 120px 96px minmax(150px, 1.3fr)";

function HeaderCell({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: text.muted }}
    >
      {children}
    </Typography>
  );
}

function RiskCell({ risk }: { risk?: RiskInfo }) {
  if (!risk) return <Typography sx={{ fontSize: 13, color: text.muted }}>—</Typography>;
  if (!risk.atRisk) return <Chip label="On track" tone="green" />;
  return <Chip label={`AT RISK ${risk.delta ?? ""}`.trim()} tone="red" />;
}

function StatusCell({ driver }: { driver: Driver }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <StatusDot status={driver.status} />
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: text.primary }}>{driver.status}</Typography>
    </Box>
  );
}

function VelocityCell({ driver }: { driver: Driver }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography sx={{ fontSize: 13, color: text.primary }}>{driver.velocityMph} mph</Typography>
      {driver.velocityTrend === "down" && <ArrowDownwardIcon sx={{ fontSize: 13, color: accent.red }} />}
    </Box>
  );
}

function appointmentText(d: Driver): { label: string; window: boolean; strict: boolean } {
  if (!d.appointment) return { label: "—", window: false, strict: false };
  if (d.appointment.kind === "window")
    return { label: `${d.appointment.start}–${d.appointment.end}`, window: true, strict: false };
  return { label: d.appointment.time ?? "—", window: false, strict: true };
}

function DriverRow({ driver, index, onOpen }: { driver: Driver; index: number; onOpen?: () => void }) {
  const appt = appointmentText(driver);
  return (
    <Box
      onClick={onOpen}
      sx={{
        display: "grid",
        gridTemplateColumns: COLUMNS,
        alignItems: "center",
        columnGap: 1.5,
        px: 2,
        py: 1.5,
        cursor: onOpen ? "pointer" : "default",
        bgcolor: index % 2 === 0 ? "transparent" : zinc[950],
        borderBottom: `1px solid ${zinc[850]}`,
        "&:hover": { bgcolor: zinc[850] },
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: text.primary }}>{driver.name}</Typography>
        <Typography sx={{ fontSize: 12, color: text.muted }}>{driver.truck}</Typography>
      </Box>
      <Typography sx={{ fontSize: 13, color: text.secondary }}>{driver.division}</Typography>
      <StatusCell driver={driver} />
      <Typography
        sx={{ fontSize: 13, color: text.secondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {driver.customer ?? "—"}
      </Typography>
      <Box>
        {driver.loadType ? (
          <Chip label={driver.loadType} tone="neutral" />
        ) : (
          <Typography sx={{ fontSize: 13, color: text.muted }}>—</Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography sx={{ fontSize: 13, color: text.secondary }}>{appt.label}</Typography>
        {appt.window && <Chip label="WINDOW" tone="neutral" />}
        {appt.strict && <Chip label="STRICT" tone="neutral" />}
      </Box>
      <RiskCell risk={driver.appointmentRisk} />
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: text.primary }}>{driver.pta?.time ?? "—"}</Typography>
      <RiskCell risk={driver.pta?.risk} />
      <Typography sx={{ fontSize: 13, color: text.secondary }}>
        {driver.hos.primaryLabel} {driver.hos.primaryValue.replace(" left", "")}
      </Typography>
      <VelocityCell driver={driver} />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {driver.tags.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: text.muted }}>—</Typography>
        ) : (
          driver.tags.map((tag) => <TagChip key={tag} tag={tag} />)
        )}
      </Box>
    </Box>
  );
}

/** The reusable board: filter pills, sortable PTA, and the all-drivers table. */
export function FreightStatusBoard({
  drivers,
  onOpenDriver,
}: {
  drivers: Driver[];
  /** Row click opens the driver's feed / message history. */
  onOpenDriver?: (driver: Driver) => void;
}) {
  const [filter, setFilter] = useState<FilterKey>("at_risk");
  const [sortAsc, setSortAsc] = useState(true);

  const rows = useMemo(() => {
    const filtered = drivers.filter((d) => matchesFilter(d, filter));
    return filtered.sort((a, b) => (sortAsc ? ptaMinutes(a) - ptaMinutes(b) : ptaMinutes(b) - ptaMinutes(a)));
  }, [drivers, filter, sortAsc]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* filter + sort row */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, px: 3, py: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {FILTERS.map((f) => (
            <Pill key={f.key} label={f.label} active={filter === f.key} onClick={() => setFilter(f.key)} />
          ))}
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => setSortAsc((v) => !v)}
          sx={{
            cursor: "pointer",
            fontFamily: "inherit",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: 13,
            fontWeight: 600,
            color: text.secondary,
            bgcolor: "transparent",
            border: `1px solid ${zinc[700]}`,
            borderRadius: "8px",
            px: 1.25,
            py: 0.75,
            whiteSpace: "nowrap",
            "&:hover": { color: text.primary, borderColor: zinc[600] },
          }}
        >
          Sort: PTA
          {sortAsc ? <ArrowUpwardIcon sx={{ fontSize: 15 }} /> : <ArrowDownwardIcon sx={{ fontSize: 15 }} />}
        </Box>
      </Box>

      {/* table */}
      <Box sx={{ flex: 1, overflow: "auto", minHeight: 0, px: 3, pb: 3 }}>
        <Box
          sx={{
            minWidth: 1180,
            border: `1px solid ${zinc[800]}`,
            borderRadius: "12px",
            overflow: "hidden",
            bgcolor: zinc[900],
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: COLUMNS,
              columnGap: 1.5,
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${zinc[700]}`,
              bgcolor: zinc[850],
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            <HeaderCell>Driver / Truck</HeaderCell>
            <HeaderCell>Div</HeaderCell>
            <HeaderCell>Status</HeaderCell>
            <HeaderCell>Customer</HeaderCell>
            <HeaderCell>Load</HeaderCell>
            <HeaderCell>Appointment</HeaderCell>
            <HeaderCell>Appt risk</HeaderCell>
            <HeaderCell>PTA</HeaderCell>
            <HeaderCell>PTA risk</HeaderCell>
            <HeaderCell>HOS</HeaderCell>
            <HeaderCell>Velocity</HeaderCell>
            <HeaderCell>Tags</HeaderCell>
          </Box>
          {rows.map((driver, i) => (
            <DriverRow
              key={driver.id}
              driver={driver}
              index={i}
              onOpen={onOpenDriver ? () => onOpenDriver(driver) : undefined}
            />
          ))}
          {rows.length === 0 && (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography sx={{ fontSize: 13, color: text.muted }}>No drivers match this filter.</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function DriverTabButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 14,
        fontWeight: 700,
        color: active ? text.primary : text.muted,
        bgcolor: "transparent",
        border: "none",
        borderBottom: `2px solid ${active ? accent.purple : "transparent"}`,
        px: 0.5,
        py: 1.5,
        transition: "color 120ms ease",
        "&:hover": { color: text.primary },
      }}
    >
      {label}
    </Box>
  );
}

function PlaceholderTab({ title }: { title: string }) {
  return (
    <Box sx={{ p: 6, textAlign: "center", color: text.muted }}>
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: text.secondary, mb: 1 }}>{title}</Typography>
      <Typography sx={{ fontSize: 13 }}>Existing drivers view — not part of this prototype.</Typography>
    </Box>
  );
}

/** Standalone wrapper (prototype page): adds the My/All/Freight tab chrome. */
export function FreightStatusView() {
  const [tab, setTab] = useState<DriverTab>("freight");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 3, px: 3, borderBottom: `1px solid ${zinc[800]}` }}>
        <DriverTabButton label="My Drivers" active={tab === "my"} onClick={() => setTab("my")} />
        <DriverTabButton label="All Drivers" active={tab === "all"} onClick={() => setTab("all")} />
        <DriverTabButton label="Freight Status" active={tab === "freight"} onClick={() => setTab("freight")} />
      </Box>
      {tab === "freight" ? (
        <FreightStatusBoard drivers={DRIVERS} />
      ) : (
        <PlaceholderTab title={tab === "my" ? "My Drivers" : "All Drivers"} />
      )}
    </Box>
  );
}
